import { useEffect } from 'react';
import type { CpGpMatchedProduct } from '../../../../../../models/Coupang';
import type { RestructuredProduct } from '../../../../../../infrastructure/SourcingClient';
import type { ImageSelection } from '../../../../../../apps/hooks/media';

interface UseImageInitializerParams {
  verifiedImages: string[];
  isInitialLoad: boolean;
  matchedProduct?: CpGpMatchedProduct;
  onInitialized: (selection: ImageSelection) => void;
}

export const useImageInitializer = ({
  verifiedImages,
  isInitialLoad,
  matchedProduct,
  onInitialized,
}: UseImageInitializerParams) => {
  useEffect(() => {
    if (!verifiedImages || verifiedImages.length === 0 || !isInitialLoad || !matchedProduct) {
      return;
    }

    // Get PDP images from /en/ folder only (not root pdp_ images)
    // Supports both formats: legacy /en/pdp_xxx_index.webp and UUID /en/pdp_uuid.webp
    const pdpImages = verifiedImages.filter((url) => {
      const filename = url.split('/').pop() || '';
      const pathSegments = url.split('/');
      const parentFolder = pathSegments[pathSegments.length - 2];

      // Must be in /en/ folder AND start with pdp_
      return parentFolder === 'en' && filename.startsWith('pdp_');
    });

    // DO NOT SORT - keep original array order
    // Images are displayed and saved in the exact order from the database
    const sortedPdpImages = pdpImages;

    // Get hero images from Coupang
    const coupangHeroImages = matchedProduct.cpDetailedProduct?.heroImageUrls || [];

    // Gallery images: Hero images that are already in VerifiedImages + all thu_ prefixed images
    const galleryImages: string[] = [];

    // 1. Add hero images that exist in VerifiedImages (legacy format)
    coupangHeroImages.forEach((heroUrl) => {
      if (verifiedImages.includes(heroUrl)) {
        galleryImages.push(heroUrl);
      }
    });

    // 2. Add all thu_ prefixed images from VerifiedImages
    verifiedImages.forEach((url) => {
      const filename = url.split('/').pop() || '';
      if (filename.startsWith('thu_') && !galleryImages.includes(url)) {
        galleryImages.push(url);
      }
    });

    // Set item-specific featured thumbnails
    const initialThumbnails: Record<string, string> = {};
    const restructuredProducts = matchedProduct?.ssRestructuredProducts || [];

    if (restructuredProducts.length > 0) {
      const productsNeedingThumbnails = restructuredProducts.filter(
        (p: RestructuredProduct) => p.type === 'variable' || p.type === 'simple',
      );

      productsNeedingThumbnails.forEach((product: RestructuredProduct) => {
        const productKey = `${product.cpItemId || ''}_${product.type}_${product.attributePack}_${product.attributeSize}_${product.attributeColor}`;

        if (product.featuredThumbnailUrl) {
          initialThumbnails[productKey] = product.featuredThumbnailUrl;
        }
      });
    }

    const initialGallery = galleryImages.length > 0 ? galleryImages : [];

    onInitialized({
      thumbnails: initialThumbnails,
      gallery: initialGallery,
      pdpImages: sortedPdpImages,
    });
  }, [verifiedImages, isInitialLoad, matchedProduct, onInitialized]);
};
